class MobileFilterMenu {
  constructor() {
    this.menu = document.querySelector('.filter-menu-wrapper');
    this.openButton = document.querySelector('#mobile-filter-open-btn');
    this.closeButton = null;
    this.sortSelect = null;
    this.filterForm = null;
    this.overlay = null;

    this.init();
  }

  init() {
    if (!this.menu) return;

    // Create overlay
    this.createOverlay();

    // Bind events
    if (this.openButton) {
      this.openButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    }

    // Find close button within the menu wrapper
    this.closeButton = this.menu.querySelector('#mobile-filter-close-btn');
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => {
        this.close();
      });
    }

    // Close on overlay click
    if (this.overlay) {
      this.overlay.addEventListener('click', () => {
        this.close();
      });
    }

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.menu.classList.contains('is-open')) {
        this.close();
      }
    });

    // Find sort select and filter form within the menu wrapper
    this.sortSelect = this.menu.querySelector('#MobileFilterSortBy');
    this.filterForm = this.menu.querySelector('#MobileFilterForm');

    // Handle sort change
    if (this.sortSelect) {
      this.sortSelect.addEventListener('change', (e) => {
        this.handleSortChange(e.target.value);
      });
    }

    // Handle filter changes
    if (this.filterForm) {
      const checkboxes = this.filterForm.querySelectorAll('input[type="checkbox"]');
      checkboxes.forEach(checkbox => {
        checkbox.addEventListener('change', () => {
          this.applyFilters();
        });
      });

      // Handle price range slider changes
      this.initPriceRangeListeners();
    }

    // Handle reset filters button
    const resetButton = this.menu.querySelector('.mobile-reset-filters');
    if (resetButton) {
      resetButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.resetFilters();
      });
    }

    // Handle individual filter remove buttons
    const removeButtons = this.menu.querySelectorAll('facet-remove a');
    removeButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const url = button.getAttribute('href');
        if (url) {
          window.location.href = url;
        }
      });
    });
  }

  handleSortChange(sortValue) {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', sortValue);
    window.location.href = url.toString();
  }

  initPriceRangeListeners() {
    // Listen to the range slider thumbs (the visual sliders)
    const rangeSliders = this.filterForm.querySelectorAll('.price-range__thumb');
    rangeSliders.forEach(slider => {
      // Use 'change' event which fires when user releases the slider
      slider.addEventListener('change', () => {
        this.applyFilters();
      });
    });

    // Also listen to the hidden/text inputs for price in case they're changed directly
    const priceInputs = this.filterForm.querySelectorAll('input[name^="filter.v.price"]');
    priceInputs.forEach(input => {
      // Skip range inputs (already handled above)
      if (input.type === 'range') return;

      input.addEventListener('change', () => {
        this.applyFilters();
      });
    });
  }

  applyFilters() {
    const url = new URL(window.location.href);
    const formData = new FormData(this.filterForm);

    // Remove existing filter params
    const keysToDelete = [];
    for (let key of url.searchParams.keys()) {
      if (key.startsWith('filter.')) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => url.searchParams.delete(key));

    // Get price input elements to check against defaults
    const priceMinInput = this.filterForm.querySelector('input[name$=".gte"]');
    const priceMaxInput = this.filterForm.querySelector('input[name$=".lte"]');
    const priceMinDefault = priceMinInput ? (priceMinInput.dataset.default || '0') : '0';
    const priceMaxDefault = priceMaxInput ? priceMaxInput.dataset.default : null;

    // Add new filter params (excluding price at default values)
    for (let [key, value] of formData.entries()) {
      // Skip price min if it's at default (0 or empty)
      if (key.endsWith('.gte')) {
        if (!value || value === '' || value === '0' || value === priceMinDefault) {
          continue;
        }
      }
      // Skip price max if it's at default (max range or empty)
      if (key.endsWith('.lte')) {
        if (!value || value === '' || value === priceMaxDefault) {
          continue;
        }
      }
      url.searchParams.append(key, value);
    }

    // Update all filter counters before navigating
    this.updateFilterCounters();

    window.location.href = url.toString();
  }

  updateFilterCounters() {
    // Count checked checkboxes
    const checkedCheckboxes = this.filterForm.querySelectorAll('input[type="checkbox"]:checked').length;

    // Check if price range has non-default values (count as 1 filter if either min or max differs from default)
    const priceMinInput = this.filterForm.querySelector('input[name$=".gte"]');
    const priceMaxInput = this.filterForm.querySelector('input[name$=".lte"]');
    const priceMin = priceMinInput ? priceMinInput.value : '';
    const priceMax = priceMaxInput ? priceMaxInput.value : '';
    const priceMinDefault = priceMinInput ? (priceMinInput.dataset.default || '0') : '0';
    const priceMaxDefault = priceMaxInput ? priceMaxInput.dataset.default : '';

    // Price counts as active only if values differ from defaults
    const minIsNonDefault = priceMin && priceMin !== '' && priceMin !== '0' && priceMin !== priceMinDefault;
    const maxIsNonDefault = priceMax && priceMax !== '' && priceMax !== priceMaxDefault;
    const hasPriceFilter = minIsNonDefault || maxIsNonDefault;

    const totalFilters = checkedCheckboxes + (hasPriceFilter ? 1 : 0);

    // Update all .filter-counter elements
    const counters = document.querySelectorAll('.filter-counter');
    counters.forEach(counter => {
      counter.textContent = `(${totalFilters})`;
    });
  }

  resetFilters() {
    const url = new URL(window.location.href);

    // Remove all filter params
    const keysToDelete = [];
    for (let key of url.searchParams.keys()) {
      if (key.startsWith('filter.')) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => url.searchParams.delete(key));

    // Close the menu
    this.close();

    // Navigate to the URL without filters
    window.location.href = url.toString();
  }

  createOverlay() {
    this.overlay = document.createElement('div');
    this.overlay.className = 'filter-menu-overlay';
    document.body.appendChild(this.overlay);
  }

  open() {
    this.menu.classList.add('is-open');
    this.overlay.classList.add('is-visible');
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.menu.classList.remove('is-open');
    this.overlay.classList.remove('is-visible');
    document.body.style.overflow = '';
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new MobileFilterMenu();
  });
} else {
  new MobileFilterMenu();
}